import React, {Suspense} from 'react';
import {observer} from "mobx-react-lite";
import {useAppState} from "@stores/AppStateContext";

import {MODALS} from "../modal-names";
import {ModalEntry} from "../../../../stores/ModalController";

const CreateWorkspace = React.lazy(() => import("../CreateWorkspace/CreateWorkspace").then(m => ({default: m.CreateWorkspace})));
const Invite = React.lazy(() => import("../Invite/Invite").then(m => ({default: m.Invite})));
const ImagePreview = React.lazy(() => import("../ImagePreview/ImagePreview").then(m => ({default: m.ImagePreview})));
const UserDeleteModal = React.lazy(() => import("../components/UserDeleteModal").then(m => ({default: m.UserDeleteModal})));
const UnsavedChangesConfirm = React.lazy(() => import("../UnsavedChangesConfirm/UnsavedChangesConfirm").then(m => ({default: m.UnsavedChangesConfirm})));
const UnsavedTakeQuizConfirm = React.lazy(() => import("../UnsavedTakeQuizConfirm/UnsavedTakeQuizConfirm").then(m => ({default: m.UnsavedTakeQuizConfirm})));
const AiQuiz = React.lazy(() => import("../AiQuiz/AiQuiz").then(m => ({default: m.AiQuiz})));
const WorkspaceConfirmDelete = React.lazy(() => import("../WorkspaceConfirmDelete/WorkspaceConfirmDelete").then(m => ({default: m.WorkspaceConfirmDelete})));
const AddMembersWorkspace = React.lazy(() => import("../AddMembersWorkspace/AddMembersWorkspace").then(m => ({default: m.AddMembersWorkspace})));
const RemoveUserFromWorkspace = React.lazy(() => import("../RemoveUserFromWorkspace/RemoveUserFromWorkspace").then(m => ({default: m.RemoveUserFromWorkspace})));
const CropModal = React.lazy(() => import("../CropModal/CropModal").then(m => ({default: m.CropModal})));
const RemoveAcknowledgmentRequire = React.lazy(() => import("../RemoveAcknowledgmentRequire/RemoveAcknowledgmentRequire").then(m => ({default: m.RemoveAcknowledgmentRequire})));
const TaskListUnitEdit = React.lazy(() => import("../TaskListUnitEdit/TaskListUnitEdit").then(m => ({default: m.TaskListUnitEdit})));
const TaskListEdit = React.lazy(() => import("../TaskListEdit/TaskListEdit").then(m => ({default: m.TaskListEdit})));
const TaskEdit = React.lazy(() => import("../TaskEdit/TaskEdit").then(m => ({default: m.TaskEdit})));
const TaskReportEdit = React.lazy(() => import("../TaskReportEdit/TaskReportEdit").then(m => ({default: m.TaskReportEdit})));
const CopyToForTaskModal = React.lazy(() => import("../CopyToForTaskModal/CopyToForTaskModal").then(m => ({default: m.CopyToForTaskModal})));
const LightBoxModal = React.lazy(() => import("../LightBoxModal/LightBoxModal").then(m => ({default: m.LightBoxModal})));
const YearCalendar = React.lazy(() => import("../YearCalendar/YearCalendar").then(m => ({default: m.YearCalendar})));
const ExceptionsCalendar = React.lazy(() => import("../ExceptionsCalendar/ExceptionsCalendar").then(m => ({default: m.ExceptionsCalendar})));
const TaskListDelete = React.lazy(() => import("../TaskListDelete/TaskListDelete").then(m => ({default: m.TaskListDelete})));
const TaskDelete = React.lazy(() => import("../TaskDelete/TaskDelete").then(m => ({default: m.TaskDelete})));
const DocumentViewModal = React.lazy(() => import("../DocumentViewModal/DocumentViewModal").then(m => ({default: m.DocumentViewModal})));
const TaskReportViewOnly = React.lazy(() => import("../TaskReportViewOnly/TaskReportViewOnly").then(m => ({default: m.TaskReportViewOnly})));
const DeleteTaskListUnit = React.lazy(() => import("../DeleteTaskListUnit/DeleteTaskListUnit").then(m => ({default: m.DeleteTaskListUnit})));
const PriceBreakdownModal = React.lazy(() => import("../PriceBreakdownModal/PriceBreakdownModal").then(m => ({default: m.PriceBreakdownModal})));
const ImportDocLazy = React.lazy(() => import('../ImportDoc/ImportDoc').then(m => ({default: m.ImportDoc})));

export const ModalManager = observer(() => {
    const appState = useAppState();
    const entries = appState.modalController.modals;
    return (
        <>
            {
                entries.map(entry => renderModal(entry))
            }
        </>
    )
});

const renderModal = ({ name, params }: ModalEntry) => {
    switch (name) {
        case MODALS.CREATE_WORKSPACE:
            return (
                <Suspense key={name} fallback={null}>
                    <CreateWorkspace/>
                </Suspense>
            );
        case MODALS.ADD_MEMBERS_WORKSPACE:
            return (
                <Suspense key={name} fallback={null}>
                    <AddMembersWorkspace {...params}/>
                </Suspense>
            );
        case MODALS.REMOVE_USER_FROM_WORKSPACE:
            return (
                <Suspense key={name} fallback={null}>
                    <RemoveUserFromWorkspace {...params}/>
                </Suspense>
            );
        case MODALS.DELETE_WORKSPACE:
            return (
                <Suspense key={name} fallback={null}>
                    <WorkspaceConfirmDelete {...params}/>
                </Suspense>
            );
        case MODALS.INVITE:
            return (
                <Suspense key={name} fallback={null}>
                    <Invite/>
                </Suspense>
            );
        case MODALS.IMAGE_PREVIEW:
            return (
                <Suspense key={name} fallback={null}>
                    <ImagePreview {...params}/>
                </Suspense>
            );
        case MODALS.USER_DELETE:
            return (
                <Suspense key={name} fallback={null}>
                    <UserDeleteModal {...params}/>
                </Suspense>
            );
        case MODALS.PRICE_BREAKDOWN:
            return (
                <Suspense key={name} fallback={null}>
                    <PriceBreakdownModal {...params}/>
                </Suspense>
            );
        case MODALS.UNSAVED_CHANGE_CONFIRM:
            return (
                <Suspense key={name} fallback={null}>
                    <UnsavedChangesConfirm {...params}/>
                </Suspense>
            );
        case MODALS.UNSAVED_CHANGE_QUIZ_TAKE_CONFIRM:
            return (
                <Suspense key={name} fallback={null}>
                    <UnsavedTakeQuizConfirm {...params}/>
                </Suspense>
            );
        case MODALS.QUIZ_AI:
            return (
                <Suspense key={name} fallback={null}>
                    <AiQuiz {...params}/>
                </Suspense>
            );
        case MODALS.REMOVE_ACKNOWLEDGMENT_REQUIRE:
            return (
                <Suspense key={name} fallback={null}>
                    <RemoveAcknowledgmentRequire {...params}/>
                </Suspense>
            );
        case MODALS.TASKLIST_UNIT_EDIT:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskListUnitEdit {...params}/>
                </Suspense>
            );
        case MODALS.TASKLIST_EDIT:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskListEdit {...params}/>
                </Suspense>
            );
        case MODALS.TASK_EDIT:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskEdit {...params}/>
                </Suspense>
            );
        case MODALS.TASK_REPORT_EDIT:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskReportEdit {...params}/>
                </Suspense>
            );
        case MODALS.TASK_REPORT_VIEW_ONLY:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskReportViewOnly {...params}/>
                </Suspense>
            );
        case MODALS.TASKLIST_UNIT_DELETE:
            return (
                <Suspense key={name} fallback={null}>
                    <DeleteTaskListUnit {...params}/>
                </Suspense>
            );
        case MODALS.TASK_LIST_DELETE:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskListDelete {...params}/>
                </Suspense>
            );
        case MODALS.TASK_DELETE:
            return (
                <Suspense key={name} fallback={null}>
                    <TaskDelete {...params}/>
                </Suspense>
            );
        case MODALS.YEAR_CALENDAR:
            return (
                <Suspense key={name} fallback={null}>
                    <YearCalendar {...params}/>
                </Suspense>
            );
        case MODALS.EXCEPTIONS_CALENDAR:
            return (
                <Suspense key={name} fallback={null}>
                    <ExceptionsCalendar {...params}/>
                </Suspense>
            );
        case MODALS.COPY_TO_FOR_TASK:
            return (
                <Suspense key={name} fallback={null}>
                    <CopyToForTaskModal {...params}/>
                </Suspense>
            );
        case MODALS.DOCUMENT_VIEW:
            return (
                <Suspense key={name} fallback={null}>
                    <DocumentViewModal {...params}/>
                </Suspense>
            );
        case MODALS.CROP:
            return (
                <Suspense key={name} fallback={null}>
                    <CropModal {...params}/>
                </Suspense>
            );
        case MODALS.LIGHTBOX:
            return (
                <Suspense key={name} fallback={null}>
                    <LightBoxModal {...params}/>
                </Suspense>
            );
        case MODALS.IMPORT_DOC:
            return (
                <Suspense key={name} fallback={null}>
                    <ImportDocLazy/>
                </Suspense>
            );
        default:
            return null;
    }
};
